import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Alert, Button, Drawer, Space, Tooltip, Typography,
} from 'antd';
import request from '../../../../../../logic/utils/request';
import getLinks from '../../../../../../logic/utils/getLinks';
import RenderInputs from './RenderInputs';
import describeSaveError from './describeSaveError';

const getMissingFields = ({ name, placename, connectedZones }) => [
  !name.trim() && 'наименование',
  !connectedZones.length && 'зону',
  !placename && 'номер для вызова',
].filter(Boolean);

function NewWorkPlace({
  isOpen, toggleOpen, fetchPlaces, placeUpdate, setPlaceData,
  placeData, initialData, setPlaceUpdate, preloader, zones, queues,
}) {
  const missingFields = getMissingFields(placeData);
  const isValid = !missingFields.length;
  const write = getLinks(true);
  const [saveError, setSaveError] = useState(null);
  const savedType = useRef(false);

  // Тип места на момент открытия — чтобы предложить вернуть его, если сервер не дал сменить
  useEffect(() => {
    if (isOpen) savedType.current = Boolean(placeData.mobile_wplace);
  }, [isOpen]);

  // Любая правка формы снимает показанную ошибку
  useEffect(() => {
    setSaveError(null);
  }, [placeData]);

  const resetModal = () => {
    setPlaceData({ ...initialData });
    toggleOpen();
    setPlaceUpdate(false);
    setSaveError(null);
  };

  const handleSaveError = (e) => setSaveError(describeSaveError(e?.data?.error ?? e?.message));

  const addPlace = async () => {
    preloader();
    try {
      await request('/server/api/wplace/', 'add', { ...placeData });
      toast.success('Выполнено');
      fetchPlaces();
      resetModal();
    } catch (e) {
      handleSaveError(e);
    }
    preloader();
  };

  const updatePlace = async () => {
    const { id, ...body } = placeData;
    preloader();
    try {
      await request(`/server/api/wplace/${id}/`, 'update', body);
      toast.success('Выполнено');
      fetchPlaces();
      resetModal();
    } catch (e) {
      handleSaveError(e);
    }
    preloader();
  };

  const savedTypeLabel = savedType.current ? 'Мобильное' : 'Статичное';

  const errorAlert = saveError && (
    <Alert
      type="error"
      showIcon
      closable
      onClose={() => setSaveError(null)}
      message={saveError.message}
      description={(
        <>
          {saveError.description}
          {saveError.details && (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {`Для поддержки: ${saveError.details}`}
            </Typography.Text>
          )}
        </>
      )}
      action={saveError.kind === 'occupiedType' && placeUpdate && (
        <Button size="small" onClick={() => setPlaceData({ ...placeData, mobile_wplace: savedType.current })}>
          {`Вернуть «${savedTypeLabel}»`}
        </Button>
      )}
    />
  );

  const footer = (
    <Space direction="vertical" style={{ width: '100%' }}>
      {errorAlert}
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={resetModal}>Отмена</Button>
        {write && (
        <Tooltip title={isValid ? '' : `Заполните: ${missingFields.join(', ')}`}>
          <Button type="primary" disabled={!isValid} onClick={placeUpdate ? updatePlace : addPlace}>
            Сохранить
          </Button>
        </Tooltip>
        )}
      </Space>
    </Space>
  );

  return (
    <>
      <div>
        <Button type="primary" size="large" onClick={toggleOpen}>Добавить</Button>
      </div>
      <Drawer
        title={placeUpdate ? 'Редактирование рабочего места' : 'Добавление рабочего места'}
        open={isOpen}
        onClose={resetModal}
        width={600}
        destroyOnClose
        footer={footer}
        bodyStyle={{ display: 'flex', flexDirection: 'column' }}
      >
        <RenderInputs
          placeData={placeData}
          setPlaceData={setPlaceData}
          zones={zones}
          queues={queues}
        />
      </Drawer>
    </>
  );
}

export default React.memo(NewWorkPlace);
